import { User, Settings, Bell, Shield, CreditCard, LogOut } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { AvatarUploadDialog } from "@/components/profile/AvatarUploadDialog";
import { useUpdateProfileMutation } from "@/store/api/userApi";
import { addNotification } from "@/store/slices/uiSlice";

import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useAuth } from "../../hooks/useAuth";
import { COUNTRIES, CURRENCIES } from "../../utils/constants";

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const dispatch = useDispatch();
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.profile.name || "",
    email: user?.email || "",
    phone: user?.profile.phone || "",
    country: user?.profile.country || "",
  });

  const [preferences, setPreferences] = useState({
    notifications: {
      email: true,
      push: true,
      sms: false,
    },
    language: "en",
    currency: "USD",
  });

  const calculateProfileCompletion = (): string => {
    // calculation logic goes here
    return "85%";
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.name.trim()) {
        dispatch(
          addNotification({
            type: "error",
            title: "Error",
            message: "Name is required",
            duration: 5000,
          })
        );
        return;
      }

      if (!formData.email.trim()) {
        dispatch(
          addNotification({
            type: "error",
            title: "Error",
            message: "Email is required",
            duration: 5000,
          })
        );
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        dispatch(
          addNotification({
            type: "error",
            title: "Invalid email",
            message: "Please enter a valid email address",
            duration: 5000,
          })
        );
        return;
      }

      // Phone validation (if provided)
      if (formData.phone && formData.phone.length < 10) {
        dispatch(
          addNotification({
            type: "error",
            title: "Invalid phone",
            message: "Please enter a valid phone number",
            duration: 5000,
          })
        );
        return;
      }

      // Call the update profile mutation
      await updateProfile({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        country: formData.country,
      }).unwrap();

      dispatch(
        addNotification({
          type: "success",
          title: "Success",
          message: "Profile updated successfully",
          duration: 5000,
        })
      );
      setIsEditing(false);
    } catch (error: any) {
      console.error("Profile update error:", error);
      const message = error?.data?.message || "Failed to update profile";
      dispatch(
        addNotification({
          type: "error",
          title: "Error",
          message,
          duration: 5000,
        })
      );
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (!user) {
    return (
      <div className='container py-8'>
        <div className='text-center'>
          <h1 className='mb-4 text-2xl font-bold'>Please Sign In</h1>
          <p className='text-muted-foreground'>You need to be signed in to view your profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='mx-auto max-w-4xl'>
        <div className='mb-8'>
          <h1 className='mb-2 text-3xl font-bold'>Profile Settings</h1>
          <p className='text-muted-foreground'>Manage your account settings and preferences</p>
        </div>

        <div className='grid grid-cols-1 gap-8 lg:grid-cols-4'>
          {/* Profile Sidebar */}
          <div className='space-y-6'>
            <Card>
              <CardContent className='p-6 text-center'>
                <AvatarUploadDialog>
                  <Avatar className='mx-auto mb-4 h-20 w-20 cursor-pointer transition-opacity hover:opacity-80'>
                    <AvatarImage src={user.profile.avatar || "/placeholder.svg"} />
                    <AvatarFallback className='text-lg'>
                      {user.profile.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </AvatarUploadDialog>
                <h3 className='font-semibold'>{user.profile.name}</h3>
                <p className='truncate text-sm text-muted-foreground'>{user.email}</p>
                <Badge variant='secondary' className='mt-2 capitalize'>
                  {user.role}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardContent className='p-4'>
                <div className='space-y-2'>
                  <div className='flex justify-between text-sm'>
                    <span className='text-muted-foreground'>Profile Complete</span>
                    <span className='font-medium'>{calculateProfileCompletion()}</span>
                  </div>
                  <div className='h-2 w-full rounded-full bg-muted'>
                    <div className='h-2 rounded-full bg-primary' style={{ width: "85%" }}></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className='lg:col-span-3'>
            <Tabs defaultValue='personal' className='w-full'>
              <TabsList className='grid w-full grid-cols-4'>
                <TabsTrigger value='personal' className='flex items-center gap-2'>
                  <User className='h-4 w-4' />
                  Personal
                </TabsTrigger>
                <TabsTrigger value='preferences' className='flex items-center gap-2'>
                  <Settings className='h-4 w-4' />
                  Preferences
                </TabsTrigger>
                <TabsTrigger value='notifications' className='flex items-center gap-2'>
                  <Bell className='h-4 w-4' />
                  Notifications
                </TabsTrigger>
                <TabsTrigger value='security' className='flex items-center gap-2'>
                  <Shield className='h-4 w-4' />
                  Security
                </TabsTrigger>
              </TabsList>

              <TabsContent value='personal' className='space-y-6'>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between'>
                    <CardTitle>Personal Information</CardTitle>
                    <Button
                      variant={isEditing ? "default" : "outline"}
                      onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
                      disabled={isUpdatingProfile}
                    >
                      {isEditing
                        ? isUpdatingProfile
                          ? "Saving..."
                          : "Save Changes"
                        : "Edit Profile"}
                    </Button>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                      <div className='space-y-2'>
                        <Label htmlFor='name'>Full Name</Label>
                        <Input
                          id='name'
                          value={formData.name}
                          onChange={e => setFormData({ ...formData, name: e.target.value })}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='email'>Email</Label>
                        <Input
                          id='email'
                          type='email'
                          value={formData.email}
                          onChange={e => setFormData({ ...formData, email: e.target.value })}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='phone'>Phone</Label>
                        <Input
                          id='phone'
                          value={formData.phone}
                          onChange={e => setFormData({ ...formData, phone: e.target.value })}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='country'>Country</Label>
                        <Select
                          value={formData.country}
                          onValueChange={value => setFormData({ ...formData, country: value })}
                          disabled={!isEditing}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder='Select country' />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map(country => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value='preferences' className='space-y-6'>
                <Card>
                  <CardHeader>
                    <CardTitle>App Preferences</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                      <div className='space-y-2'>
                        <Label>Language</Label>
                        <Select
                          value={preferences.language}
                          onValueChange={value =>
                            setPreferences({ ...preferences, language: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='en'>English</SelectItem>
                            <SelectItem value='es'>Spanish</SelectItem>
                            <SelectItem value='fr'>French</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className='space-y-2'>
                        <Label>Currency</Label>
                        <Select
                          value={preferences.currency}
                          onValueChange={value =>
                            setPreferences({ ...preferences, currency: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(CURRENCIES).map(([key, value]) => (
                              <SelectItem key={key} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value='notifications' className='space-y-6'>
                <Card>
                  <CardHeader>
                    <CardTitle>Notification Settings</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <div className='space-y-4'>
                      <div className='flex items-center justify-between'>
                        <div>
                          <Label>Email Notifications</Label>
                          <p className='text-sm text-muted-foreground'>Receive updates via email</p>
                        </div>
                        <Switch
                          checked={preferences.notifications.email}
                          onCheckedChange={checked =>
                            setPreferences({
                              ...preferences,
                              notifications: { ...preferences.notifications, email: checked },
                            })
                          }
                        />
                      </div>
                      <Separator />
                      <div className='flex items-center justify-between'>
                        <div>
                          <Label>Push Notifications</Label>
                          <p className='text-sm text-muted-foreground'>
                            Receive push notifications
                          </p>
                        </div>
                        <Switch
                          checked={preferences.notifications.push}
                          onCheckedChange={checked =>
                            setPreferences({
                              ...preferences,
                              notifications: { ...preferences.notifications, push: checked },
                            })
                          }
                        />
                      </div>
                      <Separator />
                      <div className='flex items-center justify-between'>
                        <div>
                          <Label>SMS Notifications</Label>
                          <p className='text-sm text-muted-foreground'>Receive SMS updates</p>
                        </div>
                        <Switch
                          checked={preferences.notifications.sms}
                          onCheckedChange={checked =>
                            setPreferences({
                              ...preferences,
                              notifications: { ...preferences.notifications, sms: checked },
                            })
                          }
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value='security' className='space-y-6'>
                <Card>
                  <CardHeader>
                    <CardTitle>Security Settings</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <Button variant='outline' className='w-full justify-start bg-transparent'>
                      <Shield className='mr-2 h-4 w-4' />
                      Change Password
                    </Button>
                    <Button variant='outline' className='w-full justify-start bg-transparent'>
                      <CreditCard className='mr-2 h-4 w-4' />
                      Manage Payment Methods
                    </Button>
                    <Separator />
                    <Button
                      variant='destructive'
                      onClick={handleLogout}
                      className='w-full justify-start'
                    >
                      <LogOut className='mr-2 h-4 w-4' />
                      Sign Out
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
